/**
 * Settings ▸ Model policy ▸ Bench results: move stage-bench measurements
 * between servers.
 *
 * The bench runs on a laptop (`main-bench.py export` writes the file); prod is
 * where the rules editor's suggestion needs the numbers. Upload shows what the
 * file would do entry by entry BEFORE anything is written, and importing is
 * idempotent, so the same file can go up twice without doubling anything.
 * Download writes this server's results in the same format.
 */
import { ChangeEvent, useRef, useState } from 'react';
import api from '../../services/api';
import type { BenchPreview, BenchResultsFile } from '../../types';
import { downloadName, importWouldWrite, previewSummary, readBenchFile } from '../../pipeline/benchTransfer';

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

const STATUS_STYLE: Record<string, string> = {
  new: 'text-emerald-700 dark:text-emerald-300',
  replaces: 'text-blue-700 dark:text-blue-300',
  older: 'text-gray-500 dark:text-gray-400',
  duplicate: 'text-gray-400 dark:text-gray-500',
  invalid: 'text-red-700 dark:text-red-300',
};

export default function BenchResultsTransfer() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [file, setFile] = useState<{ name: string; data: BenchResultsFile } | null>(null);
  const [preview, setPreview] = useState<BenchPreview | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (input.current) input.current.value = '';
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const data = await api.exportBenchResults();
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName(window.location.hostname, new Date());
      a.click();
      URL.revokeObjectURL(url);
      setNotice(`Downloaded ${data.entries.length} result(s).`);
    } catch (e) {
      setError(errText(e, 'Could not download the results'));
    } finally {
      setBusy(false);
    }
  };

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setError(null);
    setNotice(null);
    setPreview(null);
    const read = readBenchFile(await picked.text());
    if ('error' in read) {
      setError(read.error);
      reset();
      return;
    }
    setBusy(true);
    try {
      setFile({ name: picked.name, data: read.file });
      setPreview(await api.previewBenchResults(read.file));
    } catch (err) {
      setError(errText(err, 'Could not read that file'));
      reset();
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { counts } = await api.importBenchResults(file.data);
      setNotice(`Imported ${file.name}: ${previewSummary(counts)}.`);
      reset();
    } catch (e) {
      setError(errText(e, 'Could not import'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border rounded-lg" data-testid="bench-results-transfer">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-800 rounded-lg"
      >
        <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Bench results</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {open ? 'Hide' : 'Upload results measured elsewhere, or download these'}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-3 text-sm">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The stage bench runs locally (<span className="font-mono">main-bench.py export</span> writes a results file).
            Uploading it here gives each job's rules the bench's suggestion. It never changes a rule.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <label className={`px-3 py-1.5 bg-blue-600 text-white rounded cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
              Upload results file…
              <input ref={input} type="file" accept=".json,application/json" onChange={choose} className="hidden" />
            </label>
            <button
              type="button"
              onClick={download}
              disabled={busy}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Download this server's results
            </button>
          </div>

          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
          {notice && <p className="text-emerald-700 dark:text-emerald-300">{notice}</p>}

          {file && preview && (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3" data-testid="bench-import-preview">
              <p className="font-medium text-gray-800 dark:text-gray-100">
                {file.name}: {previewSummary(preview.counts)}
              </p>
              <div className="mt-2 max-h-72 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="font-normal py-1">job</th>
                      <th className="font-normal py-1">model</th>
                      <th className="font-normal py-1">fixture</th>
                      <th className="font-normal py-1">measured</th>
                      <th className="font-normal py-1 text-right">clean</th>
                      <th className="font-normal py-1 pl-3">import</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.map((e) => (
                      <tr key={e.index} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-1 font-mono">
                          {e.stage ?? '-'}
                          {e.archetype ? ` · ${e.archetype}` : ''}
                        </td>
                        <td className="py-1 font-mono">
                          {e.model_name ?? '-'}
                          {e.status !== 'invalid' && !e.in_pool && (
                            <span
                              className="ml-1 text-amber-700 dark:text-amber-300"
                              title="No shared, enabled provider on this server carries it; the suggestion will say so."
                            >
                              (not in pool)
                            </span>
                          )}
                        </td>
                        <td className="py-1">{e.fixture ? `${e.fixture}@v${e.version}` : ''}</td>
                        <td className="py-1">{e.measured ?? ''}</td>
                        <td className="py-1 text-right font-mono">{e.n ? `${e.clean_runs}/${e.n}` : ''}</td>
                        <td className={`py-1 pl-3 ${STATUS_STYLE[e.status] ?? ''}`}>{e.reason ?? e.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={doImport}
                  disabled={busy || !importWouldWrite(preview.counts)}
                  title={importWouldWrite(preview.counts) ? undefined : 'Everything in this file is already here or invalid.'}
                  className="px-3 py-1.5 bg-green-600 text-white rounded disabled:opacity-50"
                >
                  Import
                </button>
                <button
                  type="button"
                  onClick={reset}
                  disabled={busy}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
